import {
  OrderStatus,
  OrderType,
  PaymentMethod,
  PrinterType,
  Sort,
} from '@restaurant/types';
import { httpClient } from '../client';

export type OrderListFilter = {
  // timestamp second
  startDateTimeOrder?: number;
  endDateTimeOrder?: number;
  startDateTimePayment?: number;
  endDateTimePayment?: number;

  orderNumbers?: string[];
  statuses?: OrderStatus[];
  orderTypes?: OrderType[];
  cashierUserIds?: string[];
  paymentMethods?: PaymentMethod[];
  tableNumber?: number;
  customerName?: string;
};

export type OrderListRequest = {
  page?: number;
  size?: number;
  sort?: Sort[];
  filter?: OrderListFilter;
};

export type OrderItem = {
  menuId: string;
  name: string;
  price: number;
  quantity: number;
  totalPrice: number;
  addOns?: {
    menuId: string;
    name: string;
    price: number;
    quantity: number;
    totalPrice: number;
  }[];
  notes?: string;
  isChecked: boolean;
  custom: boolean;
};

export type OrderTax = {
  taxType: string;
  rate: number;
  amount: number;
  isApplied: boolean;
};

export type Order = {
  orderId: string;
  orderNumber: string;
  dateCreated: number; // timestamp
  status: OrderStatus;
  orderType: OrderType;
  tableNumber: number | null | undefined;
  customerName: string | null | undefined;
  delivery: OrderDelivery | null;
  isChecked: boolean;
  batches: OrderItem[][]; // nested batches
  totalItems: number;
  subtotalPrice: number;
  tax: OrderTax | null;
  totalPrice: number;

  payment?: OrderPayment;
};

export type OrderDelivery = {
  recipientName: string;
  mobileCountryCode: string;
  mobileNumber: string;
  deliveryAddress: DeliveryAddress;
  deliveryDateTime: DeliveryDateTime;
};

export interface DeliveryAddress {
  province: string;
  city: string;
  district: string;
  subdistrict: string;
  rt: string | null;
  rw: string | null;
  streetAddressLine1: string;
  streetAddressLine2: string;
  postalCode: string;
}

interface DeliveryDateTime {
  date: string | null; // YYYY-MM-DD
  time: string | null; // HH:mm
}

export type OrderPayment = {
  method: PaymentMethod;
  roundingAmount: number;
  totalAmount: number;
  cashierUserId: string;
  cashierName: string;
  timestamp: number; // usually Unix timestamp (ms or seconds)
  details: PaymentDetail[];
};

export type PaymentDetail = {
  method: PaymentMethod;
  description: string;
  cash: number;
  change: number;
  amount: number;
  timestamp: number;
};

export type Pagination = {
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
};

export type OrderListResponse = {
  pagination: Pagination;
  data: Order[];
};

const fetchOrderList = (body: OrderListRequest) => {
  return httpClient().post<OrderListResponse>('/api/v2/orders/search', body);
};

const fetchOrderById = async (id: string) => {
  return httpClient().get<Order>(`/api/v2/orders/${id}`);
};

export type CartItem = {
  menuId: string;
  quantity: number;
  addOns: {
    menuId: string;
    quantity: number;
  }[];
  notes?: string;
  custom: boolean;
  name: string;
  totalPrice: number;
};

export type CreateOrderRequest = {
  orderType: OrderType;
  tableNumber?: number | null;
  customerName?: string | null;
  items: CartItem[];
  delivery?: OrderDelivery | null;
};

const createOrder = (body: CreateOrderRequest) => {
  return httpClient().post<{ orderId: string; batchIndex: number }>(
    '/api/v2/orders',
    body
  );
};

export type AddOrderBatchRequest = {
  items: CartItem[];
};

const addOrderBatch = (orderId: string, body: AddOrderBatchRequest) => {
  return httpClient().patch<{ orderId: string; batchIndex: number }>(
    `/api/v2/orders/${orderId}/batches`,
    body
  );
};

const setOrderChecklist = (id: string, body: boolean[][]) => {
  return httpClient().post(`/api/v2/orders/${id}/checks`, {
    orderItems: body,
  });
};

const updateOrderTax = (orderId: string, isApplied: boolean) => {
  return httpClient().patch(`api/v2/orders/${orderId}/tax`, { isApplied });
};

const updateOrderInformation = (
  orderId: string,
  body: {
    orderType: string;
    tableNumber: number;
    customerName: string;
    delivery?: OrderDelivery;
  }
) => {
  return httpClient().patch(`api/v2/orders/${orderId}`, body);
};

export type PrintResponse = {
  encodedBluetoothPayload?: string;
};

const printOrderReceipt = ({
  orderId,
  printMethod,
  printerId,
}: {
  orderId: string;
  printMethod: PrinterType;
  printerId: string;
}) => {
  return httpClient().post<PrintResponse>(
    `/api/v2/orders/${orderId}/receipts`,
    {
      printMethod,
      printerId,
    }
  );
};

// indent order only
const closeOrder = ({
  orderId,
  notes,
}: {
  orderId: string;
  notes: string | undefined;
}) => {
  return httpClient().patch(`/api/v2/orders/${orderId}/close`, {
    notes,
  });
};

export const api = {
  fetchOrderList,
  fetchOrderById,
  createOrder,
  addOrderBatch,
  setOrderChecklist,
  updateOrderTax,
  updateOrderInformation,
  printOrderReceipt,
  closeOrder,
};
